import {Component} from '@angular/core';
import {MatDialog, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {FormControl, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {OpenaiService} from '../api/services/openai.service';
import {JobService} from '../api/services/job.service';
import {MatIconModule} from '@angular/material/icon';
import {MatTooltipModule} from '@angular/material/tooltip';
import {Router} from '@angular/router';
import {Job} from '../api/interfaces/jobs.dto';
import {NotificationService} from '../services/notification.service';
import {MatInputModule} from '@angular/material/input';
import {MatFormFieldModule} from '@angular/material/form-field';
import {CreditApiService} from '../api/services/credit.service';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {AiStateService} from '../services/ai-state.service';
import {GenerationStateService} from '../services/generation-state.service';
import {environment} from '../../environments/environment';
import {showSmapGenerationFailure} from './smap-generation-failure.util';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';

@Component({
  selector: 'maporium-generate-smap-prompt-dialog',
  template: `
    <div class="w-100 d-flex justify-content-between align-items-center dialog-header">
      <h2 class="align-items-center d-flex m-0 p-0">
        <span class="material-symbols-rounded me-2">cognition</span>
        {{ 'SMAP_GEN.TITLE_FROM_PROMPT' | translate }}
      </h2>
      <span class="icon-smap-action-close close-button" mat-dialog-close [matTooltip]="'SHARE_DIALOG.CLOSE_TOOLTIP' | translate"></span>
    </div>
    <div mat-dialog-content>
      <mat-form-field class="w-100" appearance="outline">
        <mat-label>{{ 'SMAP_GEN.PROMPT_LABEL' | translate }}</mat-label>
        <textarea matInput
                  [formControl]="promptControl"
                  rows="5"
                  [matTooltip]="'SMAP_GEN.PROMPT_REQUIRED_TOOLTIP' | translate"
                  [placeholder]="'SMAP_GEN.PROMPT_PLACEHOLDER' | translate"></textarea>
      </mat-form-field>
    </div>
    <div mat-dialog-actions class="d-flex align-items-center justify-content-between px-3">
      <mat-slide-toggle *ngIf="webSearchAvailable"
        class="web-search-toggle"
        [(ngModel)]="webSearchEnabled"
        labelPosition="before"
        [matTooltip]="'CREATE_MAP_DIALOG.SEARCH_WEB_TOOLTIP' | translate">
        {{ 'CREATE_MAP_DIALOG.SEARCH_WEB' | translate }}
      </mat-slide-toggle>
      <div>
        <button mat-button mat-dialog-close>{{ 'CREATE_EDIT_SMAP.ACTIONS.CANCEL' | translate }}</button>
        <button mat-button
                color="accent"
                [disabled]="!promptControl.valid"
                (click)="generate()">
          {{ 'SMAP_GEN.GENERATE' | translate }}
        </button>
      </div>
    </div>
  `,
  imports: [
    MatDialogModule,
    CommonModule,
    MatButtonModule,
    ReactiveFormsModule,
    FormsModule,
    MatTooltipModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatSlideToggleModule,
    TranslatePipe
  ],
  styles: [`
    :host {
      display: block;
    }
  `],
  standalone: true
})
export class GenerateSmapPromptDialogComponent {
  promptControl = new FormControl('', [Validators.required, Validators.minLength(3)]);
  webSearchEnabled = false;

  get webSearchAvailable(): boolean {
    return !environment.production || !this.aiStateService.isUsingCustomKeys();
  }

  constructor(
    private openaiService: OpenaiService,
    private jobService: JobService,
    private router: Router,
    private notificationService: NotificationService,
    private matDialogRef: MatDialogRef<GenerateSmapPromptDialogComponent>,
    private creditApiService: CreditApiService,
    private dialog: MatDialog,
    private aiStateService: AiStateService,
    private generationStateService: GenerationStateService,
    private translate: TranslateService,
  ) {
  }

  generate() {
    const prompt = this.promptControl.value?.trim();
    if (!prompt) {
      this.notificationService.warning(this.translate.instant('CREATE_MAP_DIALOG.ENTER_PROMPT_WARNING'));
      return;
    }

    this.creditApiService.ensureCredits(this.dialog, 'smap').subscribe(ok => {
      if (!ok) return;

      this.openaiService.generateSmapFromPrompt(prompt, this.webSearchEnabled).subscribe({
        next: (job: Job) => {
          this.notificationService.info(this.translate.instant('CREATE_MAP_DIALOG.GENERATION_IN_PROGRESS_INFO'), {
            duration: 8000,
            taskKey: job.id
          });
          this.trackGenerationJob(job, prompt);
          this.matDialogRef.close();
        },
        error: (error) => {
          const errorMessage = error.error?.message || error.message || this.translate.instant('CREATE_MAP_DIALOG.GENERATION_ERROR_DEFAULT');
          this.notificationService.error(errorMessage, {
            title: this.translate.instant('CREATE_MAP_DIALOG.GENERATION_FAILED_TITLE'),
            duration: 0
          });
          console.error('Prompt generation error:', error);
        }
      });
    });
  }

  private trackGenerationJob(job: Job, prompt: string) {
    this.generationStateService.startSmapGeneration(job.id);
    this.jobService.trackJob(job.id, (updatedJob: Job) => {
      if (updatedJob.status === 'COMPLETED') {
        this.generationStateService.endSmapGeneration(updatedJob.id);
        this.notificationService.success(
          this.translate.instant('CREATE_MAP_DIALOG.SMAP_GENERATED_MESSAGE'),
          {
            title: this.translate.instant('CREATE_MAP_DIALOG.SMAP_READY_TITLE'),
            taskKey: updatedJob.id,
            action: {
              label: this.translate.instant('CREATE_MAP_DIALOG.OPEN_SMAP'),
              handler: (onClose) => {
                if (updatedJob.result?.entityId) {
                  const entityId = updatedJob.result.entityId;
                  onClose();
                  void this.router.navigate(['/dashboard/map', entityId]);
                }
              }
            }
          }
        );
      } else if (updatedJob.status === 'FAILED') {
        this.generationStateService.endSmapGeneration(updatedJob.id);
        showSmapGenerationFailure(this.notificationService, this.translate, updatedJob.error, prompt, updatedJob.id);
      }
    });
  }
}
